/**
 * UserStatusCard Component
 * 
 * Displays user status information.
 * Shows: User ID, Participant ID, Partner Product ID
 * Bottom: Partner info (type badge, dba name), coverage dates
 * 
 * Note: Verified/Enabled badges removed - we are not the source of truth for those fields.
 */

import React from 'react';
import { Card, CardBody } from '@/components/common/Card';
import { PartnerTypeBadge } from '@/components/common/Badge';
import type { UserStatusResponse } from '@/app/types/user';
import './UserStatusCard.scss';

interface UserStatusCardProps {
  status: UserStatusResponse;
}

export const UserStatusCard: React.FC<UserStatusCardProps> = ({ status }) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card className="user-status-card">
      <CardBody>
        <div className="user-status-fields">
          <div className="user-status-field">
            <span className="user-status-label">User ID:</span>
            <span className="user-status-value">{status.community_ref_user_id}</span>
          </div>
          <div className="user-status-field">
            <span className="user-status-label">Participant ID:</span>
            <span className="user-status-value">
              {status.eligible_participant_id ?? 'N/A'}
            </span>
          </div>
          <div className="user-status-field">
            <span className="user-status-label">Partner Product ID:</span>
            <span className="user-status-value">
              {status.partner_product_id ?? 'N/A'}
            </span>
          </div>
        </div>

        {/* Bottom Section - Partner Info */}
        {(status.partner_type || status.partner_name || status.partner_dba) && (
          <div className="user-status-partner">
            <div className="user-status-partner-row">
              {status.partner_type && (
                <div className="user-status-partner-item">
                  <span className="user-status-label">Partner Type:</span>
                  <PartnerTypeBadge type={status.partner_type} />
                </div>
              )}
              {status.partner_dba && (
                <div className="user-status-partner-item">
                  <span className="user-status-label">DBA:</span>
                  <span className="user-status-value">{status.partner_dba}</span>
                </div>
              )}
              {status.partner_name && status.partner_name !== status.partner_dba && (
                <div className="user-status-partner-item">
                  <span className="user-status-label">Partner Name:</span>
                  <span className="user-status-value">{status.partner_name}</span>
                </div>
              )}
            </div>
            {(status.coverage_start_date || status.coverage_end_date) && (
              <div className="user-status-coverage">
                <span className="user-status-label">Coverage:</span>
                <span className="user-status-value">
                  {formatDate(status.coverage_start_date)} - {formatDate(status.coverage_end_date)}
                </span>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default UserStatusCard;




